import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Adds a card's explicitly selected single-variant product through Shopify Ajax.
 * The existing cart components own section refresh, count and drawer behavior.
 * @typedef {Object} QuickAddRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} label
 * @property {HTMLElement} feedback
 * @extends {Component<QuickAddRefs>}
 */
class QuickAdd extends Component {
  requiredRefs = ['button', 'label', 'feedback'];
  #listeners;
  #request;
  #resetTimer;
  #busy = false;
  #unavailable = false;
  #idle = '';

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#listeners?.abort();
    this.#request?.abort();
    this.#request = undefined;
    clearTimeout(this.#resetTimer);
    this.#busy = false;
  }

  #bind() {
    this.#cleanup();
    this.#listeners = new AbortController();
    this.#unavailable = this.refs.button.disabled && this.refs.button.getAttribute('aria-busy') !== 'true';
    this.#idle = this.refs.label.dataset.idle || this.refs.label.textContent.trim();
    this.refs.label.textContent = this.#idle;
    this.#setBusy(false);
    this.querySelector('form')?.addEventListener('submit', this.#submit, { signal: this.#listeners.signal });
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.refs.button.disabled = busy || this.#unavailable;
    if (busy) this.refs.button.setAttribute('aria-busy', 'true');
    else this.refs.button.removeAttribute('aria-busy');
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.#busy || this.#unavailable) return;
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;
    const body = new FormData(form);
    const variantId = body.get('id');
    if (!variantId) return;
    const quantity = Number(body.get('quantity')) || 1;
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    if (sectionIds.length) {
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname);
    }
    clearTimeout(this.#resetTimer);
    this.refs.feedback.textContent = '';
    this.refs.feedback.classList.remove('is-error');
    this.refs.label.textContent = this.refs.label.dataset.loading || this.#idle;
    this.#setBusy(true);
    const request = new AbortController();
    this.#request = request;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body }),
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: request.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : this.dataset.error);
      }
      if (!this.isConnected || request.signal.aborted) return;
      this.dispatchEvent(new CartAddEvent({}, String(variantId), {
        source: 'product-form-component',
        productId: this.dataset.productId,
        variantId: String(variantId),
        itemCount: quantity,
        sections: result.sections,
      }));
      this.refs.label.textContent = this.refs.label.dataset.added || this.#idle;
      this.refs.feedback.textContent = this.refs.label.textContent;
      this.#resetTimer = setTimeout(() => { this.refs.label.textContent = this.#idle; }, 2000);
      const drawer = document.querySelector('cart-drawer-component');
      try {
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else window.location.assign(this.dataset.cartUrl || Theme.routes.cart_url);
      } catch {
        // The addition already succeeded. Opening the cart page avoids a false retry.
        window.location.assign(this.dataset.cartUrl || Theme.routes.cart_url);
      }
    } catch (error) {
      if (error.name === 'AbortError' || !this.isConnected) return;
      const message = error instanceof TypeError || error instanceof SyntaxError ? this.dataset.error : error.message || this.dataset.error;
      this.refs.label.textContent = this.#idle;
      this.refs.feedback.classList.add('is-error');
      this.refs.feedback.textContent = message;
      this.dispatchEvent(new CartErrorEvent(form.id, message, message, {}));
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        if (this.isConnected) this.#setBusy(false);
      }
    }
  };
}

if (!customElements.get('se-quick-add')) customElements.define('se-quick-add', QuickAdd);
